import { create } from 'zustand';
import { send_request } from '../../utils/Request';
import { streamJsonSseWithRetry } from '../../utils/SseStreamUtil';
import {
  extractMessageAddedChatMessage,
  extractUiChunkEnvelope,
  getSseEventPayload,
  getSseEventType,
} from '../../utils/ChatEventReducer';
import { validateUiChunkEnvelope } from './conversation/stream/chunkSchema';
import { createUiChunkSessionState, applyUiChunk } from './conversation/stream/processUiChunkStream';
import useChatStreamStore from './useChatStreamStore';
import config from '../../config';

function extractRequestErrorMessage(error, fallbackMessage) {
  const payload = error?.response?.data;
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }
  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim();
    }
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return fallbackMessage;
}

function ensureHttpSuccess(response, fallbackMessage) {
  if (!response) {
    throw new Error(fallbackMessage);
  }
  if (response?.response || response?.isAxiosError) {
    throw new Error(extractRequestErrorMessage(response, fallbackMessage));
  }
  if (typeof response?.status === 'number' && response.status >= 400) {
    throw new Error(extractRequestErrorMessage(response, fallbackMessage));
  }
  return response;
}

const TERMINAL_RUN_STATUSES = new Set([
  'COMPLETED', 'FAILED', 'CANCELLED', 'SUCCESS', 'ERROR', 'DONE',
]);

function isRunTerminal(run) {
  return TERMINAL_RUN_STATUSES.has(run?.status);
}

function isRunSubscribable(run) {
  return run?.agentChatSessionId && !isRunTerminal(run);
}

// Mutable refs kept outside Zustand state — these must not trigger re-renders
let _channelSubscriptionAbort = null;
let _subscribedChannelSessionId = null;
let _threadEventAbort = null;
let _threadListStreamAbortController = null;
let _threadListTimer = null;
let _threadListRefreshTimer = null;
let _threadDetailRefreshTimer = null;
let _channelTypedStateByAgent = {};

const useChannelStore = create((set, get) => ({
  channels: [],
  channelsLoading: false,
  channelsError: null,
  threads: [],
  threadsLoading: false,
  threadsHasMore: true,
  threadsLoadingMore: false,
  threadDetail: null,
  threadDetailLoading: false,
  threadRuns: [],
  sendingMessage: false,
  thinkingAgents: [],

  executionStages: [],
  executionInProgress: false,
  channelTypedStreamingMessages: null,
  scheduledMessages: [],
  scheduledMessagesLoading: false,
  recurringSchedules: [],
  recurringSchedulesLoading: false,

  fetchChannels: async () => {
    set({ channelsLoading: true, channelsError: null });
    try {
      const rawResponse = await send_request('project-service/agent/channels', '', {}, 'GET');
      const response = ensureHttpSuccess(rawResponse, 'Failed to load channels');
      set({ channels: Array.isArray(response?.data) ? response.data : [], channelsLoading: false });
    } catch (e) {
      set({
        channelsError: e.message || 'Failed to load channels',
        channels: [],
        channelsLoading: false,
      });
    }
  },

  createChannel: async (payload) => {
    set({ channelsError: null });
    try {
      const response = ensureHttpSuccess(
        await send_request('project-service/agent/channels', payload, {}, 'POST'),
        'Failed to create channel'
      );
      const created = response?.data || null;
      if (created) {
        set((s) => ({ channels: [created, ...s.channels] }));
      }
      return created;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to create channel' });
      return null;
    }
  },

  updateChannel: async (channelId, payload) => {
    set({ channelsError: null });
    try {
      const response = ensureHttpSuccess(
        await send_request(`project-service/agent/channels/${channelId}`, payload, {}, 'PUT'),
        'Failed to update channel'
      );
      let updated = response?.data || null;
      const refreshResponse = ensureHttpSuccess(
        await send_request(`project-service/agent/channels/${channelId}`, '', {}, 'GET'),
        'Failed to load updated channel'
      );
      updated = refreshResponse?.data || updated;
      if (updated) {
        set((s) => {
          const channels = [...s.channels];
          const idx = channels.findIndex((c) => c.channelId === channelId);
          if (idx >= 0) {
            channels[idx] = updated;
          } else {
            channels.unshift(updated);
          }
          return { channels };
        });
      }
      return updated;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to update channel' });
      return null;
    }
  },

  fetchThreads: async (channelId, { limit = 25 } = {}) => {
    set({ threadsLoading: true });
    try {
      const query = limit > 0 ? { limit, offset: 0 } : {};
      const response = ensureHttpSuccess(
        await send_request(`project-service/agent/channels/${channelId}/threads`, '', query, 'GET'),
        'Failed to load channel threads'
      );
      const threads = Array.isArray(response?.data) ? response.data : [];
      set({
        threads,
        threadsLoading: false,
        threadsHasMore: limit > 0 && threads.length >= limit,
      });
    } catch (e) {
      set({
        threads: [],
        channelsError: e.message || 'Failed to load channel threads',
        threadsLoading: false,
        threadsHasMore: false,
      });
    }
  },

  fetchMoreThreads: async (channelId, { limit = 25 } = {}) => {
    const { threadsLoadingMore, threadsHasMore, threads } = get();
    if (threadsLoadingMore || !threadsHasMore) return;
    set({ threadsLoadingMore: true });
    try {
      const offset = threads.length;
      const response = ensureHttpSuccess(
        await send_request(`project-service/agent/channels/${channelId}/threads`, '', { limit, offset }, 'GET'),
        'Failed to load more threads'
      );
      const moreThreads = Array.isArray(response?.data) ? response.data : [];
      const existingIds = new Set(threads.map((t) => t.threadId));
      const dedupedMore = moreThreads.filter((t) => !existingIds.has(t.threadId));
      set({
        threads: [...threads, ...dedupedMore],
        threadsHasMore: moreThreads.length >= limit,
        threadsLoadingMore: false,
      });
    } catch (_e) {
      set({ threadsLoadingMore: false });
    }
  },

  refreshThreads: async (channelId) => {
    try {
      const currentCount = get().threads.length;
      const query = currentCount > 0 ? { limit: currentCount, offset: 0 } : {};
      const response = ensureHttpSuccess(
        await send_request(`project-service/agent/channels/${channelId}/threads`, '', query, 'GET'),
        'Failed to refresh channel threads'
      );
      set({ threads: Array.isArray(response?.data) ? response.data : [] });
    } catch (_e) {
      /* silent */
    }
  },

  fetchThread: async (channelId, threadId) => {
    set({ threadDetailLoading: true });
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/${threadId}`,
          '',
          {},
          'GET'
        ),
        'Failed to load thread'
      );
      set({ threadDetail: response?.data || null, threadDetailLoading: false });
      await get().fetchThreadRuns(channelId, threadId);
    } catch (e) {
      set({
        channelsError: e.message || 'Failed to load thread',
        threadDetail: null,
        threadDetailLoading: false,
      });
    }
  },

  refreshThread: async (channelId, threadId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/${threadId}`,
          '',
          {},
          'GET'
        ),
        'Failed to refresh thread'
      );
      if (response?.data) {
        set({ threadDetail: response.data });
      }
    } catch (_e) {
      /* silent */
    }
  },

  createThread: async (channelId, message, senderDisplayName) => {
    set({
      sendingMessage: true,
      executionStages: [],
      executionInProgress: true,
      thinkingAgents: [],
    });
    const previousThreadDetail = get().threadDetail;
    const nowIso = new Date().toISOString();
    const cleanMessage = typeof message === 'string' ? message.trim() : '';
    const optimisticThreadId = `optimistic-thread-${Date.now()}`;
    const optimisticMessageId = `optimistic-message-${Date.now()}`;
    const optimisticThread = {
      threadId: optimisticThreadId,
      channelId,
      title:
        cleanMessage.length > 80
          ? `${cleanMessage.substring(0, 80)}...`
          : cleanMessage || 'Untitled thread',
      rootMessage: cleanMessage,
      status: 'RUNNING_AGENTS',
      replyCount: 0,
      createdAt: nowIso,
      updatedAt: nowIso,
      lastReplyAt: nowIso,
      _optimistic: true,
      messages: [
        {
          messageId: optimisticMessageId,
          role: 'user',
          content: cleanMessage,
          senderDisplayName: senderDisplayName || 'You',
          timestamp: nowIso,
          _optimistic: true,
        },
      ],
    };

    set((s) => ({
      threadDetail: optimisticThread,
      threads: [
        optimisticThread,
        ...s.threads.filter((thread) => thread.threadId !== optimisticThreadId),
      ],
    }));

    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/messages`,
          { message },
          {},
          'POST'
        ),
        'Failed to create thread'
      );
      const thread = response?.data?.thread || null;
      if (thread) {
        set((s) => {
          const isOptimistic = !s.threadDetail || s.threadDetail.threadId === optimisticThreadId;
          const serverMessages = Array.isArray(thread.messages) ? thread.messages : [];
          const mergedThread = isOptimistic && serverMessages.length === 0
            ? { ...thread, messages: s.threadDetail?.messages || [] }
            : thread;
          return {
            threads: [
              thread,
              ...s.threads.filter(
                (t) =>
                  t.threadId !== thread.threadId &&
                  t.threadId !== optimisticThreadId
              ),
            ],
            threadDetail: isOptimistic ? mergedThread : s.threadDetail,
          };
        });
      }
      set({ sendingMessage: false });
      return response?.data || null;
    } catch (e) {
      set((s) => ({
        channelsError: e.message || 'Failed to create thread',
        executionInProgress: false,
        executionStages: [
          ...s.executionStages,
          {
            id: `stage-${Date.now()}-create-failed`,
            mode: null,
            stage: 'execution_request_failed',
            status: 'ERROR',
            details: { error: e.message || 'Failed to create thread' },
            timestamp: Date.now(),
          },
        ].slice(-50),
        threads: s.threads.filter(
          (thread) => thread.threadId !== optimisticThreadId
        ),
        threadDetail:
          s.threadDetail?.threadId === optimisticThreadId
            ? previousThreadDetail || null
            : s.threadDetail,
        sendingMessage: false,
      }));
      return null;
    }
  },

  appendOptimisticUserMessage: (message, senderDisplayName) => {
    set((s) => {
      if (!s.threadDetail) return {};
      const optimistic = {
        messageId: `optimistic-${Date.now()}`,
        role: 'user',
        content: message,
        senderDisplayName: senderDisplayName || 'You',
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };
      return {
        threadDetail: {
          ...s.threadDetail,
          messages: [...(s.threadDetail.messages || []), optimistic],
        },
      };
    });
  },

  sendThreadMessage: async (channelId, threadId, message) => {
    set({
      sendingMessage: true,
      executionStages: [],
      executionInProgress: true,
      thinkingAgents: [],
    });
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/${threadId}/messages`,
          { message },
          {},
          'POST'
        ),
        'Failed to send thread message'
      );
      set({ sendingMessage: false });
      return response?.data || null;
    } catch (e) {
      set((s) => ({
        channelsError: e.message || 'Failed to send thread message',
        executionInProgress: false,
        executionStages: [
          ...s.executionStages,
          {
            id: `stage-${Date.now()}-send-failed`,
            mode: null,
            stage: 'execution_request_failed',
            status: 'ERROR',
            details: { error: e.message || 'Failed to send thread message' },
            timestamp: Date.now(),
          },
        ].slice(-50),
        sendingMessage: false,
      }));
      return null;
    }
  },

  cancelThreadExecution: async (channelId, threadId) => {
    if (!channelId || !threadId) return null;
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/${threadId}/cancel`,
          {},
          {},
          'POST'
        ),
        'Failed to cancel thread execution'
      );
      set((s) => ({
        executionInProgress: false,
        thinkingAgents: [],
      
        executionStages: [
          ...s.executionStages,
          {
            id: `stage-${Date.now()}-execution-cancel-requested`,
            mode: null,
            stage: 'execution_cancel_requested',
            status: 'DONE',
            details: response?.data || {},
            timestamp: Date.now(),
          },
        ].slice(-50),
      }));
      return response?.data || null;
    } catch (e) {
      set((s) => ({
        channelsError: e.message || 'Failed to cancel thread execution',
        executionStages: [
          ...s.executionStages,
          {
            id: `stage-${Date.now()}-execution-cancel-failed`,
            mode: null,
            stage: 'execution_cancel_failed',
            status: 'ERROR',
            details: { error: e.message || 'Failed to cancel thread execution' },
            timestamp: Date.now(),
          },
        ].slice(-50),
      }));
      return null;
    }
  },

  markExecutionSkippedNoMentions: (details = {}) => {
    set((s) => ({
      thinkingAgents: [],
    
      executionInProgress: false,
      executionStages: [
        ...s.executionStages,
        {
          id: `stage-${Date.now()}-no-mentions`,
          mode: 'MENTION',
          stage: 'execution_skipped_no_mentions',
          status: 'SKIPPED',
          details,
          timestamp: Date.now(),
        },
      ].slice(-50),
    }));
  },

  scheduleThreadMessage: async (channelId, threadId, message, resumeAt) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channel/${channelId}/thread/${threadId}/schedule-message`,
          { message, resumeAt },
          {},
          'POST'
        ),
        'Failed to schedule thread message'
      );
      const data = response?.data || null;
      if (data) {
        set((s) => ({ scheduledMessages: [...s.scheduledMessages, data] }));
      }
      return data;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to schedule thread message' });
      return null;
    }
  },

  fetchScheduledMessagesByThread: async (channelId, threadId) => {
    set({ scheduledMessagesLoading: true });
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channel/${channelId}/thread/${threadId}/scheduled-messages`,
          '',
          {},
          'GET'
        ),
        'Failed to load scheduled messages'
      );
      set({
        scheduledMessages: response?.data || [],
        scheduledMessagesLoading: false,
      });
    } catch (e) {
      set({
        channelsError: e.message || 'Failed to load scheduled messages',
        scheduledMessagesLoading: false,
      });
    }
  },

  editScheduledMessage: async (scheduleId, payload) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/scheduled-message/${scheduleId}`,
          payload,
          {},
          'PUT'
        ),
        'Failed to edit scheduled message'
      );
      if (response?.data) {
        set((s) => {
          const scheduledMessages = [...s.scheduledMessages];
          const idx = scheduledMessages.findIndex((sm) => sm.scheduleId === scheduleId);
          if (idx >= 0) scheduledMessages[idx] = response.data;
          return { scheduledMessages };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to edit scheduled message' });
      return null;
    }
  },

  cancelScheduledMessage: async (scheduleId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/scheduled-message/${scheduleId}/cancel`,
          {},
          {},
          'POST'
        ),
        'Failed to cancel scheduled message'
      );
      if (response?.data) {
        set((s) => {
          const scheduledMessages = [...s.scheduledMessages];
          const idx = scheduledMessages.findIndex((sm) => sm.scheduleId === scheduleId);
          if (idx >= 0) {
            scheduledMessages[idx] = {
              ...scheduledMessages[idx],
              status: response.data.status || 'CANCELLED',
            };
          }
          return { scheduledMessages };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to cancel scheduled message' });
      return null;
    }
  },

  // ── Recurring Schedules ──

  fetchRecurringSchedulesByThread: async (channelId, threadId) => {
    set({ recurringSchedulesLoading: true });
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channel/${channelId}/thread/${threadId}/recurring-schedules`,
          '',
          {},
          'GET'
        ),
        'Failed to load recurring schedules'
      );
      set({
        recurringSchedules: response?.data || [],
        recurringSchedulesLoading: false,
      });
    } catch (e) {
      set({
        channelsError: e.message || 'Failed to load recurring schedules',
        recurringSchedulesLoading: false,
      });
    }
  },

  fetchRecurringSchedulesByAgentIds: async (agentIds) => {
    if (!agentIds || agentIds.length === 0) return;
    try {
      const results = await Promise.all(
        agentIds.map((id) =>
          send_request(
            `project-service/agent/${id}/recurring-schedules`,
            '',
            {},
            'GET'
          ).then((r) => r?.data || []).catch(() => [])
        )
      );
      const all = results.flat();
      const existing = get().recurringSchedules || [];
      const existingIds = new Set(existing.map((s) => s.scheduleId));
      const merged = [...existing, ...all.filter((s) => !existingIds.has(s.scheduleId))];
      set({ recurringSchedules: merged });
    } catch (_) {
      // thread-level fetch already handles the primary data
    }
  },

  editRecurringSchedule: async (scheduleId, payload) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/recurring-schedule/${scheduleId}`,
          payload,
          {},
          'PUT'
        ),
        'Failed to edit recurring schedule'
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex((rs) => rs.scheduleId === scheduleId);
          if (idx >= 0) recurringSchedules[idx] = response.data;
          return { recurringSchedules };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to edit recurring schedule' });
      return null;
    }
  },

  cancelRecurringSchedule: async (scheduleId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/recurring-schedule/${scheduleId}/cancel`,
          {},
          {},
          'POST'
        ),
        'Failed to cancel recurring schedule'
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex((rs) => rs.scheduleId === scheduleId);
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || 'CANCELLED',
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to cancel recurring schedule' });
      return null;
    }
  },

  pauseRecurringSchedule: async (scheduleId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/recurring-schedule/${scheduleId}/pause`,
          {},
          {},
          'POST'
        ),
        'Failed to pause recurring schedule'
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex((rs) => rs.scheduleId === scheduleId);
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || 'PAUSED',
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to pause recurring schedule' });
      return null;
    }
  },

  resumeRecurringSchedule: async (scheduleId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/recurring-schedule/${scheduleId}/resume`,
          {},
          {},
          'POST'
        ),
        'Failed to resume recurring schedule'
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex((rs) => rs.scheduleId === scheduleId);
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || 'ACTIVE',
              nextResumeAt: response.data.nextResumeAt,
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data || null;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to resume recurring schedule' });
      return null;
    }
  },

  fetchThreadRuns: async (channelId, threadId) => {
    try {
      const response = ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}/threads/${threadId}/runs`,
          '',
          {},
          'GET'
        ),
        'Failed to load thread runs'
      );
      set({ threadRuns: Array.isArray(response?.data) ? response.data : [] });
    } catch (_e) {
      set({ threadRuns: [] });
    }
  },

  subscribeToActiveRun: async (channelId, threadId, options = {}) => {
    const waitForRun = options?.waitForRun === true;
    get().disconnectStream();
    _channelTypedStateByAgent = {};

    // Abort any in-flight subscription attempt
    if (_channelSubscriptionAbort) _channelSubscriptionAbort.abort();
    const abort = new AbortController();
    _channelSubscriptionAbort = abort;

    set({
      thinkingAgents: [],
      executionStages: [],
      channelTypedStreamingMessages: null,
      executionInProgress: waitForRun ? true : false,
    });

    // Find the active run, polling if waitForRun is set
    const maxAttempts = waitForRun ? 15 : 1;
    let activeRun = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (abort.signal.aborted) return;
      if (attempt > 0) await new Promise((r) => setTimeout(r, 800));
      await get().fetchThreadRuns(channelId, threadId);
      const runs = get().threadRuns;
      activeRun = runs.find((r) => isRunSubscribable(r));
      if (activeRun) break;
      // Stop early only if all runs are terminal (nothing left to wait for)
      // Keep polling if: no runs yet (run may not exist), or runs exist without a session ID
      if (runs.length > 0 && runs.every((r) => isRunTerminal(r))) break;
    }

    if (abort.signal.aborted) return;

    if (!activeRun?.agentChatSessionId) {
      set({ executionInProgress: false });
      return;
    }

    const sessionId = activeRun.agentChatSessionId;
    _subscribedChannelSessionId = sessionId;
    set({ executionInProgress: true });

    get()._subscribeToThreadEvents(threadId);

    const result = await useChatStreamStore.getState().subscribeToSession(
      sessionId,
      sessionId,
      {
        reconnectWhileSubscribed: true,
        onEvent: (eventName, data, sid) => {
          get()._handleSessionEvent(eventName, data, sid, channelId, threadId);
        },
        shouldReconnect: () => get().executionInProgress,
        onPollFallback: () => get().refreshThread(channelId, threadId),
        pollIntervalMs: 3000,
        isSessionActive: () => get().executionInProgress,
        isSessionTerminal: () => !get().executionInProgress,
      }
    );

    _subscribedChannelSessionId = null;
    if (result?.aborted || abort.signal.aborted) return;

    // Session ended — check for additional runs (multi-agent sequential)
    await get().fetchThreadRuns(channelId, threadId);
    const nextRun = (get().threadRuns || []).find(
      (r) => isRunSubscribable(r) && r.agentChatSessionId !== sessionId
    );

    if (nextRun?.agentChatSessionId && !abort.signal.aborted) {
      _channelTypedStateByAgent = {};
      set({ channelTypedStreamingMessages: null, executionInProgress: true });
      get().subscribeToActiveRun(channelId, threadId, { waitForRun: true });
    } else {
      set({ executionInProgress: false });
      get().refreshThread(channelId, threadId);
    }
  },

  /**
   * Subscribe directly to a known session ID (returned synchronously from the backend).
   * Eliminates polling — same pattern as DM agent chat.
   */
  subscribeToSessionDirect: async (sessionId, channelId, threadId) => {
    get().disconnectStream();

    if (_channelSubscriptionAbort) {
      _channelSubscriptionAbort.abort();
    }
    const abort = new AbortController();
    _channelSubscriptionAbort = abort;

    _channelTypedStateByAgent = {};
    _subscribedChannelSessionId = sessionId;
    set({
      executionInProgress: true,
      channelTypedStreamingMessages: null,
      thinkingAgents: [],
    });

    get()._subscribeToThreadEvents(threadId);

    const result = await useChatStreamStore.getState().subscribeToSession(
      sessionId,
      sessionId,
      {
        reconnectWhileSubscribed: true,
        onEvent: (eventName, data, sid) => {
          get()._handleSessionEvent(eventName, data, sid, channelId, threadId);
        },
        shouldReconnect: () => get().executionInProgress,
        onPollFallback: () => get().refreshThread(channelId, threadId),
        pollIntervalMs: 3000,
        isSessionActive: () => get().executionInProgress,
        isSessionTerminal: () => !get().executionInProgress,
      }
    );

    _subscribedChannelSessionId = null;
    if (result?.aborted || abort.signal.aborted) return;

    // Session ended — check for additional runs (multi-agent sequential/consensus)
    await get().fetchThreadRuns(channelId, threadId);
    const nextRun = (get().threadRuns || []).find(
      (r) => isRunSubscribable(r) && r.agentChatSessionId !== sessionId
    );

    if (nextRun?.agentChatSessionId && !abort.signal.aborted) {
      _channelTypedStateByAgent = {};
      set({ channelTypedStreamingMessages: null, executionInProgress: true });
      get().subscribeToSessionDirect(nextRun.agentChatSessionId, channelId, threadId);
    } else {
      set({ executionInProgress: false });
      get().refreshThread(channelId, threadId);
    }
  },

  _handleSessionEvent: (eventName, data, sessionId, channelId, threadId) => {
    const type = getSseEventType(eventName, data);
    const payload = getSseEventPayload(data);

    if (type === 'ui_chunk') {
      const envelope = extractUiChunkEnvelope(eventName, data);
      const validation = validateUiChunkEnvelope(envelope);
      if (!validation.ok) return;
      const key = sessionId || 'default';
      if (!_channelTypedStateByAgent[key]) {
        _channelTypedStateByAgent[key] = createUiChunkSessionState(key);
      }
      _channelTypedStateByAgent[key] = applyUiChunk(
        _channelTypedStateByAgent[key],
        validation.value.chunk
      );
      const allTypedMessages = Object.values(_channelTypedStateByAgent).flatMap(
        (s) => s.messages
      );
      set({
        channelTypedStreamingMessages:
          allTypedMessages.length > 0 ? allTypedMessages : null,
      });
      return;
    }

    if (type === 'message_added') {
      set((s) => {
        const incoming = extractMessageAddedChatMessage(eventName, data);
        if (s.threadDetail?.threadId !== threadId) return {};
        if (!incoming || incoming.role === 'user' || incoming.role === 'system') return {};
        const existing = s.threadDetail.messages || [];
        const msg = {
          ...incoming,
          messageId: incoming.messageId || `sse-${Date.now()}-${Math.random()}`,
          timestamp: incoming.timestamp || new Date().toISOString(),
        };
        if (incoming.messageId && existing.some((m) => m.messageId === incoming.messageId)) {
          return {};
        }
        return {
          threadDetail: {
            ...s.threadDetail,
            messages: [...existing.filter((m) => !m._optimistic), msg],
          },
        };
      });
      return;
    }

    if (type === 'session_complete') {
      _channelTypedStateByAgent = {};
      set({
        channelTypedStreamingMessages: null,
      });
      return;
    }

    if (type === 'status_changed') {
      const nextStatus =
        typeof payload === 'string' ? payload : payload?.status;
      if (nextStatus === 'IN_PROGRESS') {
        set({ executionInProgress: true });
      }
      return;
    }

    if (type === 'snapshot') {
      const isActive = data?.streamState?.active === true;
      if (isActive) {
        set({ executionInProgress: true });
      }
      return;
    }
  },

  _subscribeToThreadEvents: (threadId) => {
    if (_threadEventAbort) _threadEventAbort.abort();
    const abort = new AbortController();
    _threadEventAbort = abort;

    const sseUrl = `${config.AI_SERVICE_BASE}/private/ai/agent/channel/threads/${threadId}/events`;

    streamJsonSseWithRetry({
      url: sseUrl,
      abortController: abort,
      maxRetries: 5,
      logLabel: 'Channel thread-events SSE',
      onEvent: (eventName, data) => {
        if (abort.signal.aborted) return;
        const type = eventName || data?.type;
        const payload = data?.payload ?? data;

        if (type === 'execution_stage') {
          set((s) => ({
            executionStages: [
              ...s.executionStages,
              {
                id: `stage-${Date.now()}-${payload?.stage || 'unknown'}`,
                mode: payload?.mode || null,
                stage: payload?.stage || type,
                status: payload?.status || 'IN_PROGRESS',
                details: payload?.details || {},
                timestamp: payload?.timestamp || Date.now(),
              },
            ].slice(-50),
          }));
          return;
        }

        if (type === 'agent_thinking') {
          const agentId = payload?.agentId;
          const agentName = payload?.agentName;
          if (agentId) {
            set((s) => {
              const already = s.thinkingAgents.some((a) => a.agentId === agentId);
              if (already) return {};
              return { thinkingAgents: [...s.thinkingAgents, { agentId, name: agentName || agentId }] };
            });
          }
          return;
        }

        if (type === 'agent_done') {
          const agentId = payload?.agentId;
          if (agentId) {
            set((s) => ({
              thinkingAgents: s.thinkingAgents.filter((a) => a.agentId !== agentId),
            }));
          }
          return;
        }

        if (type === 'thread_complete') {
          set((s) => ({
            thinkingAgents: [],
            executionInProgress: false,
            executionStages: [
              ...s.executionStages,
              {
                id: `stage-${Date.now()}-thread-complete`,
                mode: null,
                stage: 'thread_complete',
                status: 'DONE',
                details: {},
                timestamp: Date.now(),
              },
            ].slice(-50),
          }));
          get().refreshThread(
            get().threadDetail?.channelId,
            threadId
          );
          return;
        }

        if (type === 'thread_error') {
          set((s) => ({
            executionStages: [
              ...s.executionStages,
              {
                id: `stage-${Date.now()}-thread-error`,
                mode: null,
                stage: 'thread_error',
                status: 'ERROR',
                details: payload || {},
                timestamp: Date.now(),
              },
            ].slice(-50),
          }));
          return;
        }
      },
      shouldReconnect: () => !abort.signal.aborted && get().executionInProgress,
      onConnectionStateChange: () => {},
    });
  },

  disconnectStream: () => {
    if (_channelSubscriptionAbort) {
      _channelSubscriptionAbort.abort();
      _channelSubscriptionAbort = null;
    }
    if (_threadEventAbort) {
      _threadEventAbort.abort();
      _threadEventAbort = null;
    }
    if (_subscribedChannelSessionId) {
      useChatStreamStore.getState().disconnectStream();
      _subscribedChannelSessionId = null;
    }
    _channelTypedStateByAgent = {};
    set({ channelTypedStreamingMessages: null });
  },

  subscribeToThreadList: async (channelId) => {
    if (!channelId) return;
    get().disconnectThreadListStream();

    const abortController = new AbortController();
    _threadListStreamAbortController = abortController;
    const url = `${config.API_ADDRESS}/project-service/agent/channels/${channelId}/events`;

    const result = await streamJsonSseWithRetry({
      url,
      abortController,
      maxRetries: 5,
      logLabel: 'Channel thread-list SSE stream',
      onEvent: (eventName, data) =>
        get()._handleThreadListSseEvent(eventName, data, channelId),
      shouldReconnect: () => true,
    });

    if (result.aborted) return;

    if (_threadListStreamAbortController === abortController) {
      _threadListStreamAbortController = null;
    }
  },

  _handleThreadListSseEvent: (eventName, data, channelId) => {
    const parsedData = data;
    const type = getSseEventType(eventName, parsedData);
    const payload = getSseEventPayload(parsedData);
    const normalizedType = String(type || eventName || '').toLowerCase();
    const rawData =
      typeof parsedData === 'string' ? parsedData.toLowerCase() : '';

    const payloadChannelId =
      payload?.channelId ||
      payload?.channel?.channelId ||
      payload?.thread?.channelId ||
      payload?.threadChannelId ||
      payload?.metadata?.channelId;

    if (payloadChannelId && payloadChannelId !== channelId) {
      return;
    }

    if (
      normalizedType.includes('heartbeat') ||
      normalizedType.includes('keepalive') ||
      normalizedType.includes('ping') ||
      rawData === 'heartbeat' ||
      rawData === 'keepalive' ||
      rawData === 'ping'
    ) {
      return;
    }

    if (import.meta.env.DEV) {
      const eventType = String(type || eventName || 'unknown');
      let payloadPreview = '';
      if (typeof parsedData === 'string') {
        payloadPreview = parsedData;
      } else {
        try {
          payloadPreview = JSON.stringify(parsedData || {});
        } catch {
          payloadPreview = '[unserializable]';
        }
      }
      console.debug('[Channel thread-list SSE]', {
        channelId,
        eventType,
        payloadPreview: payloadPreview.slice(0, 200),
      });
    }

    get()._queueThreadListRefresh(channelId);

    const payloadThreadId =
      payload?.threadId ||
      payload?.thread?.threadId ||
      payload?.message?.threadId ||
      payload?.metadata?.threadId;
    const changedThreadIds = Array.isArray(payload?.changedThreadIds)
      ? payload.changedThreadIds
      : [];
    const activeThreadId = get().threadDetail?.threadId;
    const shouldRefreshActiveThread =
      Boolean(activeThreadId) &&
      (payloadThreadId === activeThreadId ||
        changedThreadIds.includes(activeThreadId));
    if (shouldRefreshActiveThread) {
      get()._queueThreadDetailRefresh(channelId, activeThreadId);
    }
  },

  _queueThreadListRefresh: (channelId) => {
    if (_threadListRefreshTimer) return;
    _threadListRefreshTimer = setTimeout(() => {
      _threadListRefreshTimer = null;
      get().refreshThreads(channelId);
    }, 500);
  },

  _queueThreadDetailRefresh: (channelId, threadId) => {
    if (!channelId || !threadId) return;
    if (_threadDetailRefreshTimer) return;
    if (_subscribedChannelSessionId) return;
    _threadDetailRefreshTimer = setTimeout(() => {
      _threadDetailRefreshTimer = null;
      get().refreshThread(channelId, threadId);
      get().fetchThreadRuns(channelId, threadId);
    }, 500);
  },

  disconnectThreadListStream: () => {
    if (_threadListStreamAbortController) {
      _threadListStreamAbortController.abort();
      _threadListStreamAbortController = null;
    }
    if (_threadListRefreshTimer) {
      clearTimeout(_threadListRefreshTimer);
      _threadListRefreshTimer = null;
    }
    if (_threadDetailRefreshTimer) {
      clearTimeout(_threadDetailRefreshTimer);
      _threadDetailRefreshTimer = null;
    }
  },

  startThreadListPolling: (channelId) => {
    get().stopThreadListPolling();
    _threadListTimer = setInterval(() => {
      get().refreshThreads(channelId);
    }, 10000);
  },

  stopThreadListPolling: () => {
    if (_threadListTimer) {
      clearInterval(_threadListTimer);
      _threadListTimer = null;
    }
  },

  deleteChannel: async (channelId, targetChannelId) => {
    set({ channelsError: null });
    try {
      const params = targetChannelId ? `?targetChannelId=${encodeURIComponent(targetChannelId)}` : '';
      ensureHttpSuccess(
        await send_request(
          `project-service/agent/channels/${channelId}${params}`,
          '',
          {},
          'DELETE'
        ),
        'Failed to delete channel'
      );
      set((s) => ({
        channels: s.channels.filter((c) => c.channelId !== channelId),
      }));
      return true;
    } catch (e) {
      set({ channelsError: e.message || 'Failed to delete channel' });
      return false;
    }
  },

  clearChannelsError: () => {
    set({ channelsError: null });
  },

  uploadChatAttachment: async (file, channelId) => {
    const normalizedChannelId = String(channelId || "").trim();
    if (!normalizedChannelId) throw new Error("Missing channel ID for file upload");

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("channelId", normalizedChannelId);

    const uploadResponse = await send_request(
      "project-service/project/file-library/create",
      formData,
      "",
      "POST",
      { "content-type": "multipart/form-data" }
    );

    const uploaded = uploadResponse?.data || null;
    const uploadedFileId = String(uploaded?.id || "").trim();
    if (!uploadedFileId) {
      throw new Error("Failed to upload file");
    }

    return {
      fileId: uploadedFileId,
      fileName: uploaded?.fileName || file.name,
      downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
      mimeType: uploaded?.data?.mimeType || file.type || "",
      size: uploaded?.fileSize ?? file.size ?? 0,
    };
  },

  deleteChatAttachment: async (fileId, channelId) => {
    const normalizedChannelId = String(channelId || "").trim();
    const normalizedFileId = String(fileId || "").trim();
    if (!normalizedChannelId || !normalizedFileId) return;

    await send_request(
      `project-service/project/file-library/channel/${normalizedChannelId}/deletes`,
      { deleteList: [normalizedFileId] },
      "",
      "POST"
    );
  },
}));

export default useChannelStore;
